import React, { useCallback, useEffect, useRef, useState } from "react";
import { Paperclip, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * iter69 · Vendor ↔ Pro Shop message thread, embedded on OrderDetailPage.
 * Mirrors the vendor-side thread; same collection, same attachments endpoint.
 */
export default function VendorMessagesPanel({ order }) {
  const orderId = order?.order_id;
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!orderId) return;
    try {
      const { data } = await api.get(`/admin/orders/${orderId}/vendor-messages`);
      setMessages(data || []);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "");
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true); setError("");
    try {
      const fd = new FormData();
      fd.append("body", body.trim());
      for (const f of files) fd.append("files", f);
      await api.post(`/admin/orders/${orderId}/vendor-messages`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBody(""); setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Send failed.");
    } finally { setSending(false); }
  };

  if (!orderId) return null;

  return (
    <div className="mt-6 p-6 bg-white border border-[#E5E3D8]" data-testid="admin-vendor-messages">
      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">
        Vendor Thread
      </div>
      <h3 className="font-serif text-xl text-[#1A422D] mt-0.5">Direct message with the vendor</h3>
      <div className="mt-4 space-y-3 max-h-[400px] overflow-y-auto">
        {messages.length === 0 && (
          <div className="text-sm text-[#7A7A7A]">No messages yet with any vendor on this order.</div>
        )}
        {messages.map((m) => {
          const isProShop = m.author_role === "pro_shop";
          return (
            <div key={m.message_id} className={`flex ${isProShop ? "justify-end" : "justify-start"}`} data-testid={`admin-msg-${m.message_id}`}>
              <div className={`max-w-[75%] px-4 py-3 border ${isProShop ? "bg-[#F9F8F4] border-[#1A422D]" : "bg-white border-[#B59A5A]"}`}>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8A6D2E]">
                  {isProShop ? "You (Pro Shop)" : "Vendor"} · {m.author_name || ""}
                </div>
                <div className="mt-1 text-sm text-[#1A1A1A] whitespace-pre-wrap">{m.body}</div>
                {m.attachments?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {m.attachments.map((a) => (
                      <a key={a.attachment_id}
                         href={`${process.env.REACT_APP_BACKEND_URL}/api/message-attachments/${m.message_id}/${a.attachment_id}`}
                         target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-1 text-xs text-[#1A422D] underline">
                        <Paperclip size={11} /> {a.original_filename || "attachment"}
                      </a>
                    ))}
                  </div>
                )}
                <div className="mt-1 text-[10px] text-[#7A7A7A]">{new Date(m.created_at).toLocaleString()}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 border-t border-[#F0EDE0] pt-4">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
                  placeholder="Message the vendor — ship dates, artwork proofs, revisions…"
                  data-testid="admin-vmsg-body" className="input-field" />
        <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => fileRef.current?.click()}
                    data-testid="admin-vmsg-attach"
                    className="inline-flex items-center gap-1 text-xs text-[#1A422D] hover:text-[#B59A5A]">
              <Paperclip size={13} /> Attach files
            </button>
            <input ref={fileRef} type="file" multiple hidden
                   accept="image/*,application/pdf"
                   onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            {files.length > 0 && <span className="text-xs text-[#7A7A7A]">{files.length} file(s) queued</span>}
          </div>
          <button type="button" onClick={send} disabled={sending || !body.trim()}
                  data-testid="admin-vmsg-send"
                  className="btn-primary text-xs inline-flex items-center gap-1">
            <Send size={12} /> {sending ? "Sending…" : "Send"}
          </button>
        </div>
        {error && <div className="mt-2 text-xs text-[#a33]">{error}</div>}
      </div>
    </div>
  );
}
